/* globals data, leagueAverageDataFilteredGrouped, serverParams */
import 'bam-node-baseball-savant/leaderboard/utils/multi-select'
// import Table from 'bam-node-savant-table/table'
import Table, { Header, SortDirection } from 'bam-node-savant-table/table-ts.ts'
import { guff } from 'bam-node-baseball-savant/utils/guff'

import concatGroupedHeaders from '../utils/concat-grouped-headers'
import getSplitHeaders from '../utils/get-grouped-headers'
import getDynamicAggregateFirstHeaders from '../utils/dynamic-aggregate-first-headers'
import { BattedBallRow } from '../swing-path/types/globals.ts'

const tableDivId = 'batted_ball_table'

const isPitchingType = ['pitcher', 'pitching-team'].includes(serverParams.type)

const isLeague = serverParams.type === 'league'

const batter_link = 'stats=statcast-r-hitting-mlb#batted_ball_profile'
const pitcher_link = 'stats=statcast-r-hitting-mlb#batted_ball_profile'

const firstHeaders = getDynamicAggregateFirstHeaders(
    serverParams.type,
    isPitchingType ? pitcher_link : batter_link
)

const headers: Header<BattedBallRow>[] = [
    ...firstHeaders,

    {
        name: 'BBE',
        field: 'num_bbe',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball'),
        toolTip: 'Batted Ball<br />Events',
    },
    {
        name: 'GB %',
        field: 'gb_rate',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted<br />balls hit on ground',
    },
    {
        name: 'AIR %',
        field: 'air_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted<br />balls hit in air',
    },
    {
        name: 'FB %',
        field: 'fb_rate',
        headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
        fieldClass: `tr-data align-center dashed-left`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted<br />balls that are fly balls',
    },
    {
        name: 'LD %',
        field: 'ld_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted<br />balls that are line drives',
    },
    {
        name: 'PU %',
        field: 'pu_rate',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted<br />balls that are popups',
    },
    {
        name: 'Pull %',
        field: 'pull_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted<br />balls that are pulled',
    },
    {
        name: 'Straight %',
        field: 'straight_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted balls<br />that are hit up the middle',
    },
    {
        name: 'Oppo %',
        field: 'oppo_rate',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip: '% of all batted balls<br />that are hit the opposite way',
    },
    {
        name: 'Pull<br />GB %',
        field: 'pull_gb_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip:
            '% of all batted balls<br />that are pulled<br />on the ground',
    },
    {
        name: 'Straight<br />GB %',
        field: 'straight_gb_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip:
            '% of all batted balls<br />that are hit up the middle<br />on the ground',
    },
    {
        name: 'Oppo<br />GB %',
        field: 'oppo_gb_rate',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip:
            '% of all batted balls<br />that are hit the opposite way<br />on the ground',
    },
    {
        name: 'Pull<br />AIR %',
        field: 'pull_air_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        highlight: isPitchingType ? [0.3, 0.17, 0.0] : [0.0, 0.17, 0.3],
        toolTip: '% of all batted balls<br />that are pulled in the air',
    },
    {
        name: 'Straight<br />AIR %',
        field: 'straight_air_rate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip:
            '% of all batted balls<br />that are hit up the middle<br />in the air',
    },
    {
        name: 'Oppo<br />AIR %',
        field: 'oppo_air_rate',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'batted_ball_rate'),
        toolTip:
            '% of all batted balls<br />that are hit the opposite way<br />in the air',
    },
]

const groupHeaders = getSplitHeaders(serverParams.secondarySplit)

// We don't include the first two (rk. and name), but do include Team (optional) and BBE
function getFirstColspan() {
    // the default
    let colspan = isLeague ? 1 : 2

    // Add the extra group by columns
    colspan += groupHeaders.length

    return colspan
}

const secondaryHeaders = [
    // Rank
    {
        name: '',
        colspan: 1,
        headerClass: 'th-name-header',
    },
    // Name (Static)
    {
        name: '',
        colspan: 1,
        headerClass: 'th-name-header table-static-column',
    },
    {
        name: '',
        colspan: getFirstColspan(),
        headerClass: 'th-name-header',
    },
    // Above GB%, includes line below
    {
        name: '',
        colspan: 1,
        headerClass: 'th-title-header',
    },
    {
        name: 'AIR = FB + LD + PU',
        colspan: 4,
        headerClass: 'th-title-header border-right',
    },
    {
        name: 'All Batted Balls',
        colspan: 3,
        headerClass: 'th-title-header border-right',
    },
    {
        name: 'Batted Ball Breakdown',
        colspan: 6,
        headerClass: 'th-title-header',
    },
]

let showLeagueAverage = false

function loadTable(
    filteredData: BattedBallRow[],
    existingSort: keyof BattedBallRow | null = null,
    existingSortDir: SortDirection | null = null
) {
    const el = document.getElementById(tableDivId)

    if (!el) {
        throw new Error(`could not find table div wrapper ${tableDivId}`)
    }

    const withPinnedData = [
        ...filteredData,
        ...leagueAverageDataFilteredGrouped.map((d) => ({
            ...d,
            pinned: true,
        })),
    ]
    const width = el.offsetWidth

    let repeatHeader = 0
    if (width <= 768) {
        repeatHeader = 15
    }

    const currentParams = new URLSearchParams(window.location.search)
    const sortOrderByType = ['pitcher', 'pitching-team'].includes(
        currentParams.get('type') || ''
    )
        ? 'asc'
        : 'desc'

    const defaultSort =
        existingSort ||
        (currentParams.get('sortColumn') as keyof BattedBallRow) ||
        'pull_air_rate'

    const defaultSortDirection: SortDirection =
        existingSortDir ||
        (currentParams.get('sortDirection') as SortDirection) ||
        sortOrderByType

    const table = new Table<BattedBallRow>({
        id: tableDivId,
        repeatHeader,
        headers: concatGroupedHeaders(headers, groupHeaders),
        secondaryHeaders,
        highlightFalsy: true,
        data: withPinnedData,
        hasPinned: true,
        showPinned: showLeagueAverage,
        defaultSort,
        defaultSortDirection: defaultSortDirection || sortOrderByType,
        showRank: true,
        sortCallback(column, _, { state }) {
            const url = new URL(window.location.href)
            url.searchParams.set('sortColumn', String(column))
            url.searchParams.set('sortDirection', state || 'desc')
            window.history.replaceState('', '', url.toString())
        },
    })

    return table
}

if (data) {
    const table = loadTable(data as BattedBallRow[])

    const $leagueAverageToggle = document.getElementById(
        'btn-toggle-league-average'
    )

    $leagueAverageToggle?.addEventListener('click', () => {
        showLeagueAverage = !showLeagueAverage

        table.setShowPinned(showLeagueAverage)
    })
}
